import { useEffect, useMemo } from 'react';

import { UseBaseTable } from 'hooks/useBaseTable';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import {
    arrayBufferToString,
    generateTabs,
} from 'components/Dashboards/Project/Components/VersionCompare/utils/helper';
import { UseCriticalPathRemovedProvider } from 'components/widgets/CriticalPathRemoved/CriticalPathRemoved.provider';

interface Props {
    ui: UseCriticalPathRemovedProvider['ui'];
    api: UseCriticalPathRemovedProvider['api'];
    title: string;
    subTitle: string;
    table: UseBaseTable['table'];
    isSinglePage: boolean;
}

export const useCriticalPathRemovedLogic = ({ ui, api, title, subTitle, table, isSinglePage }: Props) => {
    const { handleSnackBar } = useCustomSnackBar();
    const { sendEventWithDimensions } = useEventWithDimensions();

    const handleAPIExcelExport = () => {
        api.exportCsvReport.instance.mutate(api.exportCsvReport.payload, {
            onSuccess: () => {
                handleSnackBar('File Successfully Downloaded', 'success');

                sendEventWithDimensions({
                    category: 'Export',
                    action: 'Excel',
                    label: `${title} ${subTitle}`,
                });
            },
            onError: (error) => {
                const message = arrayBufferToString(error?.response?.data);
                DEFAULT_ERROR({ error, message, handleError: handleSnackBar });
            },
        });
    };

    const activeData = useMemo(() => {
        if (
            api.getCriticalPathRemovedTableData.instance.data &&
            api.getCriticalPathRemovedTableData.instance?.data.periodItemsList
        ) {
            const periodItemsList = api.getCriticalPathRemovedTableData.instance?.data.periodItemsList;
            return (
                periodItemsList.find((item) => item.id === ui.state.activeTab)?.itemsList ||
                periodItemsList[0].itemsList
            );
        }
        return [];
    }, [api.getCriticalPathRemovedTableData.instance.data, ui.state.activeTab]);

    const tableData = useMemo(() => {
        const statusArray = ui.state.statusFilter?.map((item) => item.value);

        if (activeData) {
            return activeData.filter((item) =>
                statusArray?.includes(item.latestVersion.cardBoardViewResponse.status.name),
            );
        }
        return null;
    }, [activeData, ui.state.statusFilter]);

    useEffect(() => {
        if (isSinglePage) {
            table.setPagination({ page: 1, size: tableData?.length });
        }
    }, [isSinglePage, tableData]);

    const tabsArrData = generateTabs({ data: api.getCriticalPathRemovedTableData.instance?.data });

    return {
        logic: {
            tableData,
            tabsArrData,
            handleAPIExcelExport,
        },
    };
};
